import React, { useEffect, useState } from 'react';
import {
  isEmpty,
  PopupWindow,
  SelectInput,
  Size,
} from '@utahdts/dts-react-common';
import Button from '@utahdts/dts-react-common/components/form/button/Button';
import appStore from '../app/mobx/appStore';
import webservice from '../app/webservice/webservice';
import isEqual from 'lodash/isEqual';
import { usePrevious } from './hooks/usePrevious';
import { useForceUpdate } from './hooks/useForceUpdate';
import security from '../app/roles/security';
import roles from '../app/roles/roles';
import { autorun } from 'mobx';
import { observer } from 'mobx-react';
import { statuses } from './Shared';
import menuStore from '../app/mobx/menuStore';
import { debugLog } from '../app/debug/devLogger';

const CompanyPopup = () => {
  const [inputValue, setInputValue] = useState(appStore.currentOrgRecord);
  const prevUser = usePrevious(appStore.loggedInUser);
  const forceUpdate = useForceUpdate();

  const onClosePopup = () => {
    appStore.selectOrgRecord(inputValue);
    appStore.showMessage = false;
    menuStore.setupMenu();
    forceUpdate();
  };

  const onChange = (field, value) => {
    setInputValue(appStore.userOrgs.find((org) => org.fein === value));
  };

  // Role names the signed-in user holds for a given org.
  const rolesForOrg = (org) =>
    appStore.loggedInUser.roles
      .filter(
        (role) =>
          role.org === org.fein ||
          role.fein === org.fein ||
          role.org === org.id,
      )
      .map((role) => role.role);

  // Shape an org document into a company-picker option.
  const toOrgOption = (org) => ({
    ...org,
    roles: rolesForOrg(org),
    label: org.name || org.fein,
    value: org.fein,
  });

  const setOrg = () => {
    appStore.currentOrg = undefined;
    if (
      !appStore.loggedInUser?.roles ||
      appStore.loggedInUser.roles.length === 0
    ) {
      appStore.showMessage = true;
      return;
    }

    const orgs = [
      ...new Set(
        appStore.loggedInUser.roles.reduce((result, role) => {
          const fein = role.fein || role.org;
          !isEmpty(fein) && result.push(fein);
          return result;
        }, []),
      ),
    ];

    if (orgs.length > 1) {
      // Skip re-fetching ONLY when userOrgs already holds fully-resolved options
      // (a name/label per role fein). At login, setLoggedInUser seeds userOrgs
      // with stub `{ fein }` placeholders that have no label/value/status; those
      // MUST still be resolved so the picker shows company names instead of an
      // empty dropdown.
      const alreadyResolved =
        appStore.userOrgs.length === orgs.length &&
        orgs.every((fein) =>
          appStore.userOrgs.some(
            (org) => org && org.fein === fein && (org.label || org.name),
          ),
        );
      if (alreadyResolved) {
        appStore.showMessage = true;
        return;
      }
      const options = [];
      // Resolve each org independently: a single failed/denied fetch must not
      // sink the whole batch (Promise.all would otherwise reject and leave the
      // user unresolved), so each query swallows its own error.
      const queries = orgs.map((orgId) =>
        webservice.company
          .get(orgId)
          .then((org) => {
            if (org?.status === statuses.APPROVED) {
              options.push(toOrgOption(org));
            }
          })
          .catch(() => undefined),
      );
      Promise.all(queries).then(() => {
        if (options.length > 1) {
          appStore.userOrgs = options;
          appStore.showMessage = true;
          forceUpdate();
        } else if (options.length === 1) {
          appStore.selectOrgRecord(options[0]);
          menuStore.setupMenu();
        } else {
          appStore.showMessage = true;
          forceUpdate();
        }
      });
      return;
    }

    webservice.company
      .get(orgs[0])
      .then((org) => {
        if (org?.status === statuses.APPROVED) {
          appStore.selectOrgRecord(toOrgOption(org));
          menuStore.setupMenu();
        } else {
          appStore.showMessage = true;
          forceUpdate();
        }
      })
      .catch(() => {
        appStore.showMessage = true;
        forceUpdate();
      });
  };

  useEffect(() => {
    if (!isEqual(prevUser?.roles, appStore.loggedInUser?.roles)) {
      // The session-info / company-picker gate only runs for NON admin/dev
      // users. If a global Administrator lands here it means the backend
      // resolved them to a role-less pharma-user (e.g. the token could not be
      // linked to their document), which is exactly what surfaces the "not
      // associated with any approved company" notice. Log the decision so it
      // can be confirmed straight from the browser console (enable with
      // `window.__UID_DEBUG = true`).
      const isAdminOrDev = security.hasAnyRole([roles.dev, roles.admin]);
      debugLog('roles', 'CompanyPopup role gate', {
        email: appStore.loggedInUser?.email,
        roles: (appStore.loggedInUser?.roles || []).map((role) => role?.role),
        isAdminOrDev,
        willRunCompanyGate: !isAdminOrDev,
      });
      !isAdminOrDev && setOrg();
    }
  }, [appStore.loggedInUser]);

  useEffect(
    () =>
      autorun(() => {
        if (appStore.showMessage) forceUpdate();
      }),
    [appStore.showMessage],
  );

  return (
    // Only show the session-info / company-picker modal for an AUTHENTICATED
    // user. When signed out (the "Log in with UtahID" screen is showing),
    // loggedInUser is null and the modal must never appear.
    appStore.showMessage &&
    appStore.loggedInUser && (
      <PopupWindow
        keyName="CompanyPopupUnique"
        footerChildren={
          appStore.userOrgs.length > 0
            ? [
                <Button
                  key={'blah'}
                  label="Okay"
                  disabled={!inputValue}
                  className="message-popup__button-okay"
                  onClick={() => onClosePopup()}
                />,
              ]
            : [
                <Button
                  key={'blah'}
                  label="Okay"
                  className="message-popup__button-okay"
                  onClick={() => {
                    // A signed-in org admin with no resolvable approved company
                    // must NOT be force-signed-out (that bounces them back to
                    // the login screen). Dismiss the notice instead; the menu
                    // still offers Sign Out.
                    appStore.showMessage = false;
                    menuStore.setupMenu();
                    forceUpdate();
                  }}
                />,
              ]
        }
      >
        <div className="atid_message_popup_content_wrapper message-popup__content-wrapper">
          <div className="message-popup__message-wrapper flex1">
            <h3>Session info</h3>
            {appStore.userOrgs.length > 0 ? (
              <>
                <p>
                  This account is associated with multiple companies. <br />
                  Please select the company used for this session. <br />
                  You can switch anytime in the menu.
                </p>
                <SelectInput
                  showLabel={false}
                  label="Current company"
                  field="currentOrg"
                  value={inputValue}
                  onChange={onChange}
                  size={Size.FULL_WIDTH}
                  options={appStore.userOrgs}
                />
              </>
            ) : (
              <p>
                This account is not associated with any approved company. <br />
                The Utah Insurance Department might be reviewing your
                registration. Please try again later.
              </p>
            )}
          </div>
        </div>
      </PopupWindow>
    )
  );
};

export default observer(CompanyPopup);
